/** Player entity: wires all player components and handles movement, combat, dash, and skill input. */
import Phaser from 'phaser';
import { Entity } from './Entity';
import { PhysicsComponent } from '../components/PhysicsComponent';
import { InputComponent } from '../components/InputComponent';
import { HealthComponent } from '../components/HealthComponent';
import { StaminaComponent } from '../components/StaminaComponent';
import { DashComponent } from '../components/DashComponent';
import { CombatComponent } from '../components/CombatComponent';
import { HitboxComponent } from '../components/HitboxComponent';
import { AttackVisualComponent } from '../components/AttackVisualComponent';
import { DashTrailComponent } from '../components/DashTrailComponent';
import { DashAttackVisualComponent } from '../components/DashAttackVisualComponent';
import { PunchImpactComponent } from '../components/PunchImpactComponent';
import { FistDebugComponent } from '../components/FistDebugComponent';
import { SkillProgressionComponent } from '../components/SkillProgressionComponent';
import { AttunementComponent } from '../components/AttunementComponent';
import { SkillActivationComponent } from '../components/SkillActivationComponent';
import { PunchmoreGlowComponent } from '../components/PunchmoreGlowComponent';
import { PunchmoreImpactComponent } from '../components/PunchmoreImpactComponent';
import { ConfigLoader } from '../systems/ConfigLoader';
import { DebugConfig } from '../debug/DebugConfig';
import { DebugLog } from '../debug/DebugLog';
import type { HideSkillEffect } from '../skills/player/HideSkillEffect';
import type { Position } from '../utils/Types';
import { ANIM_FRAME_RATE, PLAYER_BODY, SPRITE_DISPLAY_STANDARD } from './SpriteLayout';
import { PlayerAnimationController } from './PlayerAnimationController';
import { magnitude } from '../utils/Math';
import type { StatusEffectTracker } from '../systems/StatusEffectTracker';
import { PlayerStateComponent } from '../components/PlayerStateComponent';

/** The player-controlled character with full movement, combat, skill, and attunement systems. */
export class Player extends Entity {
  private physics: PhysicsComponent;
  private input: InputComponent;
  health: HealthComponent;
  stamina: StaminaComponent;
  dash: DashComponent;
  combat: CombatComponent;
  hitbox: HitboxComponent;
  private attackVisual: AttackVisualComponent;
  private dashTrail: DashTrailComponent;
  private dashAttackVisual: DashAttackVisualComponent;
  private punchImpact: PunchImpactComponent;
  private fistDebug: FistDebugComponent;
  skillProgression: SkillProgressionComponent;
  attunement: AttunementComponent;
  skillActivation: SkillActivationComponent;
  private punchmoreGlow: PunchmoreGlowComponent;
  private punchmoreImpact: PunchmoreImpactComponent;
  /** Source-keyed state toggles (movement lock, DR, knockback/stagger immunity, petrified). */
  playerState: PlayerStateComponent = new PlayerStateComponent();
  facingDirection: Position = { x: 0, y: 1 };
  private animController = new PlayerAnimationController();
  private isRunning: boolean = false;
  private wasRunHeld: boolean = false;
  private runRequestTime: number = 0;
  private lastLogPos = { x: 0, y: 0, time: 0 };

  /** Optional hook to resolve stealth movement speed (e.g. Glide upgrade). */
  resolveStealthSpeed?: (baseMultiplier: number) => number;

  /** Multiplier applied to all movement speeds (walk, run, stealth). Default 1.0. */
  movementSpeedMultiplier: number = 1.0;

  /** Set simulated movement input override (for headless-sim tests). Pass null to clear. */
  setSimMovementOverride(override: { up: boolean; down: boolean; left: boolean; right: boolean } | null): void {
    this.input.simMovementOverride = override;
  }

  /** Fraction of max HP regenerated per second while concealed (hide/stealth). Default 0 (no regen). */
  concealedHealthRegenRate: number = 0;

  /** Per-entity status effect tracker. Created by GameScene via StatusEffectSystem.createTracker(). */
  statusEffectTracker: StatusEffectTracker | null = null;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    const sprite = scene.add.sprite(x, y, 'player-sprite', 0);
    // Scale sprite frames to display size (character occupies ~30% of frame)
    sprite.setScale(SPRITE_DISPLAY_STANDARD / sprite.frame.width);
    super(scene, sprite);

    this.createAnimations(scene);

    this.physics = new PhysicsComponent(scene, this.sprite);
    this.physics.body.setSize(PLAYER_BODY.sizeW, PLAYER_BODY.sizeH);
    this.physics.body.setOffset(PLAYER_BODY.offsetX, PLAYER_BODY.offsetY);

    this.input = new InputComponent(scene);
    this.health = new HealthComponent(this.events, ConfigLoader.combat.player.maxHp);
    this.stamina = new StaminaComponent(this.events);
    this.dash = new DashComponent(this.events, this.stamina);
    this.combat = new CombatComponent(this.events);
    this.hitbox = new HitboxComponent(scene, sprite);
    this.attackVisual = new AttackVisualComponent(scene, sprite, this.events, this.facingDirection);
    this.dashTrail = new DashTrailComponent(scene, sprite, this.events);
    this.dashAttackVisual = new DashAttackVisualComponent(scene, sprite, this.events, this.facingDirection);
    this.punchImpact = new PunchImpactComponent(scene, sprite, this.events, this.facingDirection);
    this.fistDebug = new FistDebugComponent(scene, sprite, this.facingDirection);

    this.skillProgression = new SkillProgressionComponent(this.events);
    this.attunement = new AttunementComponent(this.events, this.skillProgression);
    this.skillActivation = new SkillActivationComponent(this.events, this.stamina);
    this.skillActivation.setSkillProgression(this.skillProgression);
    this.punchmoreGlow = new PunchmoreGlowComponent(scene, sprite, this.events, this.facingDirection);
    this.punchmoreImpact = new PunchmoreImpactComponent(scene, sprite, this.events, this.facingDirection);

    this.addComponent('physics', this.physics);
    this.addComponent('input', this.input);
    this.addComponent('health', this.health);
    this.addComponent('stamina', this.stamina);
    this.addComponent('dash', this.dash);
    this.addComponent('combat', this.combat);
    this.addComponent('attackVisual', this.attackVisual);
    this.addComponent('dashTrail', this.dashTrail);
    this.addComponent('dashAttackVisual', this.dashAttackVisual);
    this.addComponent('punchImpact', this.punchImpact);
    this.addComponent('fistDebug', this.fistDebug);
    this.addComponent('skillProgression', this.skillProgression);
    this.addComponent('attunement', this.attunement);
    this.addComponent('skillActivation', this.skillActivation);
    this.addComponent('playerState', this.playerState);
    this.addComponent('punchmoreGlow', this.punchmoreGlow);
    this.addComponent('punchmoreImpact', this.punchmoreImpact);

    // Listen for attack state changes to manage hitbox
    this.events.on('attack-state-changed', this.onAttackStateChanged, this);
    // Hit flash on damage (not heals)
    let lastHp = this.health.hp;
    this.events.on('health-changed', (hp: number) => {
      if (hp < lastHp) {
        this.animController.onDamage();
      }
      lastHp = hp;
    });
  }

  /** The Arcade physics body for collision and velocity control. */
  get body(): Phaser.Physics.Arcade.Body {
    return this.physics.body;
  }

  /** Whether the player is currently in the running state (stamina draining, speed boosted). */
  get running(): boolean {
    return this.isRunning;
  }

  private get animSprite(): Phaser.GameObjects.Sprite {
    return this.sprite as Phaser.GameObjects.Sprite;
  }

  /** Activate the hitbox on the 'active' phase and deactivate it on any other state change. */
  private onAttackStateChanged(state: string, attackType: string): void {
    if (state === 'active') {
      this.hitbox.activate(this.facingDirection.x, this.facingDirection.y, attackType);
    } else {
      this.hitbox.deactivate();
    }
  }

  /** Register walk animations (south, north, east) on the scene's animation manager; no-ops if already registered. */
  private createAnimations(scene: Phaser.Scene): void {
    if (scene.anims.exists('player-walk-south')) return;

    // New 7×3 spritesheet: rows=south/north/east, cols=idle/walk-1/walk-2/attack-windup/attack-strike/hit/death
    scene.anims.create({
      key: 'player-walk-south',
      frames: [{ key: 'player-sprite', frame: 0 }, { key: 'player-sprite', frame: 1 }, { key: 'player-sprite', frame: 0 }, { key: 'player-sprite', frame: 2 }],
      frameRate: ANIM_FRAME_RATE,
      repeat: -1,
    });

    scene.anims.create({
      key: 'player-walk-north',
      frames: [{ key: 'player-sprite', frame: 7 }, { key: 'player-sprite', frame: 8 }, { key: 'player-sprite', frame: 7 }, { key: 'player-sprite', frame: 9 }],
      frameRate: ANIM_FRAME_RATE,
      repeat: -1,
    });

    scene.anims.create({
      key: 'player-walk-east',
      frames: [{ key: 'player-sprite', frame: 14 }, { key: 'player-sprite', frame: 15 }, { key: 'player-sprite', frame: 14 }, { key: 'player-sprite', frame: 16 }],
      frameRate: ANIM_FRAME_RATE,
      repeat: -1,
    });
  }

  /** Process input each frame: movement, skill activation, dash, and animation. */
  update(delta: number): void {
    // Suppress stamina recharge during active attack windows only.
    if (this.combat.isAttacking) {
      this.stamina.blockRecharge();
    }

    super.update(delta);

    // Concealed health regen (Sanctuary passive)
    if (this.concealedHealthRegenRate > 0 && !this.health.isDead) {
      const isConcealed = this.skillActivation.isSkillActive('hide') || this.skillActivation.isSkillActive('stealth');
      if (isConcealed) {
        const regenAmount = this.health.max * this.concealedHealthRegenRate * (delta / 1000);
        this.health.heal(regenAmount);
      }
    }

    // Movement lock: suppress all movement input and zero velocity
    if (this.playerState.isMovementLocked) {
      this.physics.setVelocity(0, 0);
      this.stamina.setStationary(true);
      this.combat.facingDirection = this.facingDirection;
      this.animController.update(this.animSprite, 0, 0, delta, this.combat.isAttacking, this.combat.state, false, this.facingDirection.x, this.facingDirection.y);
      return;
    }

    const { state } = this.input;
    const movementConfig = ConfigLoader.movement;

    // Read movement input
    let moveX = 0;
    let moveY = 0;
    if (state.left) moveX -= 1;
    if (state.right) moveX += 1;
    if (state.up) moveY -= 1;
    if (state.down) moveY += 1;

    // Normalize diagonal
    if (moveX !== 0 && moveY !== 0) {
      const factor = Math.SQRT1_2;
      moveX *= factor;
      moveY *= factor;
    }

    this.stamina.setStationary(moveX === 0 && moveY === 0);

    // Track facing direction (persists when standing still)
    if (moveX !== 0 || moveY !== 0) {
      this.facingDirection.x = moveX;
      this.facingDirection.y = moveY;
    }

    // Sync facing to combat component
    this.combat.facingDirection = this.facingDirection;

    // --- Skill key inputs: all 5 slots dispatch through attunement ---
    const skillKeys = [state.skill1, state.skill2, state.skill3, state.skill4, state.skill5];
    for (let i = 0; i < skillKeys.length; i++) {
      if (skillKeys[i]) {
        const s = this.attunement.getSkillAtSlot(i);
        if (s) this.skillActivation.dispatch(s);
      }
    }

    // --- Dash: can cancel attack recovery ---
    if (state.dash && !this.dash.canDash) {
      DebugLog.throttled('player-dash-blocked', 200, 'physics',
        `DASH blocked: charges=${this.dash.charges}/${this.dash.maxCharges} isDashing=${this.dash.isDashing}`);
    }
    if (state.dash && this.dash.canDash) {
      // Dashing breaks hide (cascades to stealth via callback) unless suppressed (e.g. Ghost Dash)
      if (!this.dash.suppressDashBreaksStealth) {
        this.breakHideIfActive();
      }

      // Dash-cancel from recovery
      if (this.combat.isInRecovery) {
        this.combat.dashCancel();
      }

      const dashX = (moveX !== 0 || moveY !== 0) ? moveX : this.facingDirection.x;
      const dashY = (moveX !== 0 || moveY !== 0) ? moveY : this.facingDirection.y;
      this.dash.startDash(dashX, dashY);
    }

    // --- During dash: override velocity ---
    if (this.dash.isDashing) {
      this.physics.setVelocity(
        this.dash.dashDirection.x * this.dash.speed,
        this.dash.dashDirection.y * this.dash.speed
      );
      return;
    }

    // --- During attack: movement varies by phase ---
    if (this.combat.isAttacking && this.combat.state !== 'recovery') {
      // Allow slow movement during wind-up, stop during active; recovery falls through to normal movement
      const attackSpeedFactor = this.combat.state === 'windup' ? ConfigLoader.combat.input.windupMovementFactor : 0;
      const speed = movementConfig.walk.speed * attackSpeedFactor;
      this.physics.setVelocity(moveX * speed, moveY * speed);

      // Update hitbox position to track player
      if (this.combat.state === 'active') {
        this.hitbox.updatePosition(this.facingDirection.x, this.facingDirection.y);
      }
      this.animController.update(this.animSprite, moveX, moveY, delta, this.combat.isAttacking, this.combat.state, this.isRunning, this.facingDirection.x, this.facingDirection.y);
      return;
    }

    // --- Run or walk ---
    let speed = movementConfig.walk.speed;
    const runRequested = state.run && (moveX !== 0 || moveY !== 0);

    // Track run request edge — log the moment run is first requested
    if (runRequested && !this.wasRunHeld) {
      this.runRequestTime = performance.now();
      DebugLog.log('physics', `RUN requested: stamina=${this.stamina.current.toFixed(1)}/${this.stamina.max} stealth=${this.skillActivation.isSkillActive('stealth')} blocked=${!this.isActuallyMoving(moveX, moveY)} attacking=${this.combat.isAttacking} dashing=${this.dash.isDashing}`);
    }

    // Running while stealthed breaks stealth/hide (unless debug flag) and runs normally
    if (this.skillActivation.isSkillActive('stealth') && state.run && (moveX !== 0 || moveY !== 0) && !DebugConfig.flags.runInStealth) {
      this.breakHideIfActive();
    }

    // Stealth overrides run — slow walk only (unless debug runInStealth + holding run)
    const wasRunning = this.isRunning;
    this.isRunning = false;
    const debugRun = DebugConfig.flags.runInStealth && state.run && (moveX !== 0 || moveY !== 0);
    if (this.skillActivation.isSkillActive('stealth') && !debugRun) {
      const baseMult = ConfigLoader.stealthTuning.movementSpeedMultiplier;
      const stealthMult = this.resolveStealthSpeed ? this.resolveStealthSpeed(baseMult) : baseMult;
      speed = movementConfig.walk.speed * stealthMult;
      this.stamina.stopDraining();
    } else if (state.run && !this.stamina.isEmpty && (moveX !== 0 || moveY !== 0)
      && this.isActuallyMoving(moveX, moveY)) {
      speed = movementConfig.walk.speed * movementConfig.run.speedMultiplier;
      this.isRunning = true;
      this.stamina.drain();
    } else {
      this.stamina.stopDraining();
      // Log why run is blocked when the player is holding the run key
      if (runRequested) {
        const reasons: string[] = [];
        if (this.stamina.isEmpty) reasons.push(`stamina=0/${this.stamina.max}`);
        else reasons.push(`stamina=${this.stamina.current.toFixed(1)}/${this.stamina.max}`);
        if (!this.isActuallyMoving(moveX, moveY)) reasons.push('wall-blocked');
        if (this.skillActivation.isSkillActive('stealth')) reasons.push('stealth-active');
        DebugLog.throttled('player-run-blocked', 200, 'physics',
          `RUN blocked: ${reasons.join(', ')}`);
      }
    }

    // Log the moment run actually activates
    if (this.isRunning && !wasRunning) {
      const lag = this.runRequestTime > 0 ? (performance.now() - this.runRequestTime).toFixed(0) : '?';
      DebugLog.log('physics', `RUN activated: lag=${lag}ms speed=${speed.toFixed(0)} stamina=${this.stamina.current.toFixed(1)}/${this.stamina.max}`);
    }
    if (!this.isRunning && wasRunning) {
      DebugLog.log('physics', `RUN deactivated: stamina=${this.stamina.current.toFixed(1)}/${this.stamina.max}`);
    }

    this.wasRunHeld = runRequested;

    speed *= this.movementSpeedMultiplier;
    this.physics.setVelocity(moveX * speed, moveY * speed);

    // Log actual velocity after physics application (skip when idle)
    const body = this.physics.body;
    const actualSpeed = magnitude(body.velocity.x, body.velocity.y);
    const targetSpeed = (moveX !== 0 || moveY !== 0) ? speed : 0;
    if (actualSpeed > 0 || targetSpeed > 0) {
      const now = performance.now();
      const elapsed = now - this.lastLogPos.time;
      if (elapsed >= 500) {
        const dx = body.position.x - this.lastLogPos.x;
        const dy = body.position.y - this.lastLogPos.y;
        const dist = magnitude(dx, dy);
        const pxPerSec = elapsed > 0 ? (dist / elapsed * 1000) : 0;
        DebugLog.log('physics',
          `vel=${actualSpeed.toFixed(0)} (target=${targetSpeed.toFixed(0)}) ACTUAL=${pxPerSec.toFixed(0)}px/s (${dist.toFixed(1)}px/${elapsed.toFixed(0)}ms) run=${this.isRunning} stamina=${this.stamina.current.toFixed(1)}/${this.stamina.max} dash=${this.dash.charges}/${this.dash.maxCharges} dt=${delta.toFixed(1)}ms fps=${(1000/delta).toFixed(0)}`);
        this.lastLogPos.x = body.position.x;
        this.lastLogPos.y = body.position.y;
        this.lastLogPos.time = now;
      }
    }

    // --- Animation (priority: hit flash > attack > walk > idle) ---
    this.animController.update(this.animSprite, moveX, moveY, delta, this.combat.isAttacking, this.combat.state, this.isRunning, this.facingDirection.x, this.facingDirection.y);
  }

  /** Freeze the player and return corpse info so the caller can create a standalone corpse sprite. */
  playDeath(): { frame: number; flipX: boolean; scaleX: number; scaleY: number } {
    const { frame, flipX } = this.animController.getDeathFrame(this.facingDirection.x, this.facingDirection.y);

    // Clear hit flash so it doesn't override the death frame
    this.animController.reset();

    // Freeze physics — stop all movement and disable the body
    this.physics.body.setVelocity(0, 0);
    this.physics.body.enable = false;

    // Clear hitstop white tint (applied by HitstopSystem.freeze)
    this.animSprite.clearTint();

    // Stop animation and hide — the caller creates a standalone corpse sprite
    this.animSprite.anims.stop();
    this.animSprite.setAlpha(0);
    this.animSprite.setVisible(false);

    return { frame, flipX, scaleX: this.animSprite.scaleX, scaleY: this.animSprite.scaleY };
  }

  /** Check if the player can actually move in the intended direction (not fully blocked by walls). */
  private isActuallyMoving(moveX: number, moveY: number): boolean {
    const b = this.physics.body;
    const blockedX = moveX !== 0 && ((moveX > 0 && b.blocked.right) || (moveX < 0 && b.blocked.left));
    const blockedY = moveY !== 0 && ((moveY > 0 && b.blocked.down) || (moveY < 0 && b.blocked.up));
    // At least one axis of intended movement is unblocked
    return (moveX !== 0 && !blockedX) || (moveY !== 0 && !blockedY);
  }

  /** Force-break the Hide skill if it is currently active, which cascades to break Stealth as well. */
  private breakHideIfActive(): void {
    if (!this.skillActivation.isSkillActive('hide')) return;
    const hide = this.skillActivation.getEffect<HideSkillEffect>('hide');
    hide?.breakHide();
  }

  /** Reset the player to full health at a new position after death. */
  respawn(x: number, y: number): void {
    // Re-enable physics
    this.physics.body.enable = true;
    this.physics.body.setVelocity(0, 0);

    // Teleport — body.reset() takes sprite position (it calls setPosition internally)
    this.physics.body.reset(x, y);

    // Restore sprite (re-enable visibility hidden by playDeath)
    this.animSprite.setVisible(true);
    this.animSprite.clearTint();
    this.animSprite.setAlpha(1);
    this.animSprite.anims.stop();
    const { frame: idleFrame, flipX } = this.animController.getIdleFrame(this.facingDirection.x, this.facingDirection.y);
    this.animSprite.setFrame(idleFrame);
    this.animSprite.setFlipX(flipX);

    // Clear hit flash so it doesn't trigger on respawn
    this.animController.reset();

    // Clear lingering status effects from previous life
    this.statusEffectTracker?.removeAll();

    // Full heal
    this.health.setHp(this.health.max);
  }

  /** Unregister event listeners and destroy all player-owned objects. */
  destroy(): void {
    this.events.off('attack-state-changed', this.onAttackStateChanged, this);
    this.hitbox.destroy();
    this.attackVisual.destroy();
    super.destroy();
  }
}
